import React, {
  memo,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CategoryItem,
  CategoryNavigationItems,
} from './CategoryNavigationItems/CategoryNavigationItems';
import {NavigationFlyoutContentWrapper} from './NavigationFlyoutContent/NavigationFlyoutContentWrapper';

import {CategoryIdentifier} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/category_identifier.types';
import {Flyout} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/navigation_bar.types';
import {GetHeaderBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {COLORS, HandleClickOutside} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import {getColorSetHexes} from '../../../utils/colorSet';
import {NAVIGATION_HEIGHT} from '../constants';
import {RowSectionStyled} from '../RowSectionStyled';
import {SearchBarOpenOrigin} from '../SearchBar/hooks/useSearchBarDisplayState';
import {SearchSuggestions} from '../SearchBar/SearchSuggestions';
import {useCloseFlyoutContentOnRouteChange} from './hooks/useCloseFlyoutContentOnRouteChange';
import {useHeaderHints} from './hooks/useHeaderHints';
import {NavigationFlyoutContents} from './NavigationFlyoutContent/NavigationFlyoutContents';

export type NavigationItemsType = Omit<Flyout, 'containers'> | undefined;

export type TouchOrMouseEventHandler<Element = HTMLElement> =
  React.EventHandler<React.MouseEvent<Element> | React.TouchEvent<Element>>;

const checkIfEqual = (
  first?: CategoryIdentifier,
  second?: CategoryIdentifier,
) => {
  if (first?.identifier?.$case === 'id' && second?.identifier?.$case === 'id') {
    return first.identifier.id === second.identifier.id;
  }
  if (
    first?.identifier?.$case === 'legacyPath' &&
    second?.identifier?.$case === 'legacyPath'
  ) {
    return first.identifier.legacyPath === second.identifier.legacyPath;
  }

  return false;
};

export interface TopCategoryNavigationViewProps {
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchWrapperRef: React.RefObject<HTMLDivElement | null>;
  isSearchInTabletMode: boolean;
  isSearchBarExpanded: boolean;
  collapseSearchBar: VoidFunction;
  expandSearchBar: VoidFunction;
  onExtendedChange?: (isExtended: boolean) => void;
  openOrigin?: SearchBarOpenOrigin | null;
}

const TopCategoryNavigationView: React.FC<
  GetHeaderBulkResponse['navBar'] & TopCategoryNavigationViewProps
> = ({
  items,
  searchInputRef,
  searchWrapperRef,
  isSearchInTabletMode,
  isSearchBarExpanded,
  collapseSearchBar,
  expandSearchBar,
  onExtendedChange,
  openOrigin,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const {activeNavItemIndex, openFlyout, closeFlyout} = useActiveFlyout();
  useCloseFlyoutContentOnRouteChange(closeFlyout);

  const onEnterNavigationItem = useCallback(
    (index: number) => () => openFlyout(index),
    [openFlyout],
  );

  /**
   * This enhances the hints on the client with user specific hints
   */
  const headerHints = useHeaderHints();

  const mappedItems: CategoryItem[] = useMemo(
    () =>
      items.map(item => {
        const mappedItem: CategoryItem = {
          ...item,
        };

        if (headerHints.data && headerHints.data?.type) {
          const hint = headerHints.data?.categories.find(category =>
            checkIfEqual(category, item.category),
          );
          if (hint) {
            const hintType = headerHints.data.type;
            mappedItem.icon =
              hintType.$case === 'coupon'
                ? {
                    variant: 'coupon',
                    'data-testid': 'couponIcon',
                    color: COLORS.PrimaryCoupon,
                  }
                : {
                    variant: 'sale',
                    'data-testid': 'saleIcon',
                    color: hintType.discount.colors.backgroundColor.hex,
                    foregroundColor:
                      hintType.discount.colors.foregroundColor.hex,
                  };
          }
          return mappedItem;
        }

        // we only want those sale icons if there are no icons coming back from the header hints endpoint
        if (item.saleIcon && !headerHints.data?.type) {
          const {backgroundColor, foregroundColor} = getColorSetHexes(
            item.saleIcon.colors,
            {backgroundFallback: item.saleIcon.color.hex},
          );
          mappedItem.icon = {
            variant: 'sale',
            'data-testid': 'saleIcon',
            color: backgroundColor,
            foregroundColor,
          };
        }

        return mappedItem;
      }),
    [headerHints.data, items],
  );

  return (
    <TopCategoryNavigationViewWrapper
      ref={wrapperRef}
      onMouseLeave={closeFlyout}
      data-testid="headerNavigation"
    >
      {activeNavItemIndex !== undefined && (
        <HandleClickOutside wrapperRef={wrapperRef} onClose={closeFlyout} />
      )}

      <Wrapper>
        <CategoryNavigationItems
          activeNavItemIndex={activeNavItemIndex}
          items={mappedItems}
          onEnterNavigationItem={onEnterNavigationItem}
          isSearchBarExpanded={isSearchBarExpanded}
        />
        <SearchSuggestions
          searchInputRef={searchInputRef}
          searchWrapperRef={searchWrapperRef}
          isSearchInTabletMode={isSearchInTabletMode}
          isSearchBarExpanded={isSearchBarExpanded}
          expandSearchBar={expandSearchBar}
          collapseSearchBar={collapseSearchBar}
          onExtendedChange={onExtendedChange}
          openOrigin={openOrigin}
          expandToCenter
        />
      </Wrapper>

      <Suspense>
        <NavigationFlyoutContentWrapper
          isOpen={
            activeNavItemIndex !== undefined &&
            !!items[activeNavItemIndex]?.flyout
          }
        >
          <NavigationFlyoutContents
            activeNavItemIndex={activeNavItemIndex}
            items={items}
          />
        </NavigationFlyoutContentWrapper>
      </Suspense>
    </TopCategoryNavigationViewWrapper>
  );
};

const TopCategoryNavigationViewMemo = memo(TopCategoryNavigationView);

export {TopCategoryNavigationViewMemo as TopCategoryNavigationView};

const useActiveFlyout = () => {
  const [activeNavItemIndex, dispatcher] = useState<undefined | number>(
    undefined,
  );

  const openFlyout = useCallback((value: number) => {
    dispatcher(value);
  }, []);
  const closeFlyout = useCallback(() => {
    dispatcher(undefined);
  }, []);

  return {
    activeNavItemIndex,
    openFlyout,
    closeFlyout,
  };
};

const TopCategoryNavigationViewWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${NAVIGATION_HEIGHT}px;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
`;

const Wrapper = styled(RowSectionStyled)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
`;
